import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AUDIO_PADRAO, tocar } from '../helpers/salaAudio';

// Sink de audio persistente da sala de voz. Fica montado dentro do SalaProvider
// (que vive acima do router) para que a reproducao de audio remoto sobreviva
// a troca de rota — antes disso o audio so tocava pelos <video> dos tiles em
// Sala.js e, ao sair de /ti/sala, aqueles elementos sumiam do DOM e o usuario
// deixava de ouvir os outros ainda que a conexao WebRTC continuasse viva.
//
// Coordenacao com os tiles: quando a rota atual e /ti/sala, os <audio> deste
// sink ficam `muted` porque o <video> do tile ja toca o audio da mesma stream;
// tocar dos dois lados geraria eco. Fora de /ti/sala o sink desmuta e assume
// sozinho a reproducao. O handoff acontece na hora da navegacao — nenhum gap
// perceptivel, pois o sink ja estava com srcObject setado e apenas alterna
// o `muted`.

function ParticipanteAudio({ participante, saidaAudio, ajuste, muted }) {
  const audioRef = useRef(null);
  const { stream } = participante;

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream || null;
    // Trocar a fonte nao retoma a reproducao sozinho — ver `tocar`.
    if (stream) tocar(el, participante.nome);
  }, [stream, participante.nome]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !saidaAudio || typeof el.setSinkId !== 'function') return;
    el.setSinkId(saidaAudio).catch(() => { /* id sumiu (fone desplugado) */ });
  }, [saidaAudio, stream]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const vol = typeof ajuste?.volume === 'number' ? ajuste.volume : AUDIO_PADRAO.volume;
    el.volume = Math.min(Math.max(vol, 0), 1);
  }, [ajuste?.volume]);

  return (
    <audio
      ref={audioRef}
      autoPlay
      playsInline
      muted={muted}
      data-peer-id={participante.peerId}
    />
  );
}

export default function SalaAudioSink({ participantes = [], saidaAudio, audioAjustes = {} }) {
  const location = useLocation();
  // Na propria pagina da sala o <video> do tile ja reproduz — mudo aqui para
  // nao duplicar o audio (eco). Em qualquer outra rota, o sink e a unica
  // fonte de audio remoto e desmuta.
  const emSala = location.pathname === '/ti/sala';

  return (
    <div aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
      {participantes
        .filter((p) => !!p.stream)
        .map((p) => (
          <ParticipanteAudio
            key={p.peerId}
            participante={p}
            saidaAudio={saidaAudio}
            ajuste={audioAjustes[p.usuarioId]}
            muted={emSala}
          />
        ))}
    </div>
  );
}
